'use client';

import throttle from 'lodash.throttle';
import { useCallback, useEffect, useState } from 'react';

export interface UseInfiniteScrollConfig {
  bottomOffset?: number;
  throttleWait?: number;
  blockScrollHeight: number | undefined;
}

export enum ScrollDirections {
  DOWN = 'DOWN',
  UP = 'UP',
}

export function useInfiniteScroll(
  config?: UseInfiniteScrollConfig
): [boolean, () => void, () => void] {
  const {
    bottomOffset = 100,
    throttleWait = 150,
    blockScrollHeight = 0,
  } = config || {};

  const [atTheBottom, setAtTheBottom] = useState(false);

  // eslint-disable-next-line
  const handleScroll = useCallback(
    throttle(() => {
      if (
        atTheBottom ||
        blockScrollHeight > bottomOffset ||
        document.documentElement.offsetHeight -
          ((document.scrollingElement || document.documentElement).scrollTop +
            window.innerHeight) >
          bottomOffset
      )
        return;

      setAtTheBottom(true);
    }, throttleWait),
    [atTheBottom, throttleWait, bottomOffset]
  );

  useEffect(() => {
    if (!atTheBottom) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);

      // initial handle call if screen is bigger than initial content height
      handleScroll();

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [handleScroll, atTheBottom]);

  const continueScroll = () => setAtTheBottom(false);

  return [atTheBottom, continueScroll, handleScroll];
}

export function useScrollBlock(isOff?: boolean, disableHook?: boolean) {
  useEffect(() => {
    if (disableHook) return;

    const body = document.body;

    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const topOffset = window.pageYOffset;

    const bodyPaddingRight =
      parseInt(
        window.getComputedStyle(body).getPropertyValue('padding-right')
      ) || 0;

    if (body.style.overflow === 'hidden') return;

    const block = () => {
      body.style.overflow = 'hidden';
      // body.style.position = 'fixed'; // is position fixed lazy loading in modals doesn't work. if position static there is scroll of body for ios
      body.style.paddingRight = `${bodyPaddingRight + scrollBarWidth}px`;

      body.style.position = 'fixed';
      body.style.top = `-${topOffset}px`;
      // body.style.bottom = '0';
      body.style.right = '0';
      body.style.left = '0';
    };

    const unblock = () => {
      body.style.removeProperty('overflow');
      body.style.paddingRight = '';

      body.style.position = 'static';
      body.style.top = 'auto';
      body.style.bottom = 'auto';
      body.style.right = 'auto';
      body.style.left = 'auto';
      window.scrollTo(0, topOffset);
    };

    if (isOff) {
      unblock();
    } else {
      block();
    }

    return () => {
      unblock();
    };
  }, [isOff, disableHook]);
}
